/**
 * Contribution models for admin management
 *
 * These interfaces match the backend API responses.
 */

export enum ContributionStatus {
  PENDING_PAYMENT = 'PENDING_PAYMENT',
  PAID = 'PAID',
  FAILED = 'FAILED',
  CANCELED = 'CANCELED',
  REFUNDED = 'REFUNDED',
}

export enum PaymentMethod {
  CARD = 'CARD',
  BANK_TRANSFER = 'BANK_TRANSFER',
}

export interface ProjectContribution {
  id: string;
  projectId: string;
  projectName: string;
  percentage: number;
  co2Compensated: number;
  amount: number;
  pricePerTonne: number;
  project?: Project;
}

export interface Project {
  id: string;
  name: string;
  description?: string;
  pricePerTonne: number;
  status: string;
}

export interface UserEmail {
  id: string;
  email: string;
  isPrimary: boolean;
}

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  emails: UserEmail[];
}

export interface Company {
  id: string;
  name: string;
}

export interface Trip {
  id: string;
  title: string;
  travellers: number;
  totalCO2?: number;
}

export enum OdooSyncStatus {
  PENDING = 'PENDING',
  SYNCED = 'SYNCED',
  FAILED = 'FAILED',
}

export interface Contribution {
  id: string;
  status: ContributionStatus;
  createdAt: string;
  updatedAt: string;
  totalAmount: number;
  paymentMethod?: PaymentMethod;
  stripePaymentIntentId?: string;
  paidAt?: string;
  paidBy?: string;
  adminNotes?: string;
  certificateRecipientName: string;
  certificateS3Key?: string;
  invoiceS3Key?: string;
  odooSyncStatus?: OdooSyncStatus;
  odooInvoiceId?: string;
  isSingleTraveler: boolean;
  globalPercentage: number;

  // Relations
  user: User;
  company: Company;
  trip: Trip;
  projectContributions: ProjectContribution[];

  // Bank transfer details (only for pending bank transfers)
  bankDetails?: BankDetails;
}

export interface ContributionFilters {
  search?: string;
  status?: ContributionStatus;
  paymentMethod?: PaymentMethod;
  startDate?: Date;
  endDate?: Date;
}

export interface PaginationParams {
  page: number;
  limit: number;
}

export interface PaginationMetadata {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: PaginationMetadata;
  /** Available statuses for filtering (only returned for company scope) */
  availableStatuses?: ContributionStatus[];
}

/**
 * Bank transfer details for pending bank transfer contributions
 * Only included when paymentMethod === 'BANK_TRANSFER' && status === 'PENDING_PAYMENT'
 */
export interface BankDetails {
  iban: string;
  bic: string;
  accountName: string;
  reference: string;
  amount: number;
}

// ============================================
// VAT BREAKDOWN TYPES (for contribution preview)
// ============================================

/**
 * VAT breakdown for API responses
 * Used in contribution preview and details
 */
export interface VatBreakdown {
  amountExcludingVat: number; // HT - amount before VAT
  vatRate: number; // 0.21 or 0.00
  vatAmount: number; // TVA amount
  totalAmount: number; // TTC - amount including VAT
  isReverseCharge: boolean; // True if EU reverse charge applies
  reverseChargeNote?: string; // "Autoliquidation TVA UE" if applicable
}

/**
 * Project allocation in preview response
 */
export interface ProjectAllocation {
  projectId: string;
  projectName: string;
  pricePerTonne: number;
  percentage: number;
  co2Compensated: number; // kg
  amount: number; // EUR
}

/**
 * Preview contribution response (before creation)
 */
export interface ContributionPreviewResponse {
  co2Compensated: number;
  projectAllocations: ProjectAllocation[];
  vatBreakdown: VatBreakdown;
}
